import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const TYPE = { entreno: "bg-train-soft text-train-ink", ejercicio: "bg-train-soft text-train-ink", volumen: "bg-streak-soft text-streak-ink", comida: "bg-food-soft text-food-ink" };

const ChangeDay = ({ c, checked, onToggle, i }) => (
  <button type="button" onClick={onToggle} data-testid={`ai-change-day-${i}`} className={`w-full rounded-2xl border-2 p-4 text-left transition-colors ${checked ? "border-ink bg-white" : "border-stone-200 bg-stone-50 opacity-60"}`}>
    <div className="flex items-center justify-between">
      <p className="font-display font-bold">{c.label}</p>
      <span className={`flex h-6 w-6 items-center justify-center rounded-md border-2 ${checked ? "border-ink bg-ink text-white" : "border-stone-300"}`}>{checked && <Check className="h-4 w-4" />}</span>
    </div>
    <p className="mt-1 text-sm text-stone-600">{c.reason}</p>
    <ul className="mt-3 space-y-1.5">
      {c.items.map((it, j) => (
        <li key={j} className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className={`rounded-full px-2 py-0.5 font-semibold capitalize ${TYPE[it.type]}`}>{it.type}</span>
          {it.before && <span className="text-stone-400 line-through">{it.before}</span>}
          {it.before && <ArrowRight className="h-3 w-3 text-stone-400" />}
          <span className="font-semibold text-ink">{it.after}</span>
        </li>
      ))}
    </ul>
  </button>
);

export const AiPlanDialog = ({
  open,
  onOpenChange,
  initialRequest = "",
  autoGenerate = false,
  onApplied,
  title,
  description,
}) => {
  const [request, setRequest] = useState(initialRequest);
  const [prop, setProp] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const autoRan = useRef(false);

  const show = (p) => { setProp(p); setSel(p ? p.changes.map((c) => c.date) : []); };

  useEffect(() => {
    if (!open) {
      autoRan.current = false;
      return;
    }
    setRequest(initialRequest);
    if (autoGenerate) {
      show(null);
      setBusy(true);
      return;
    }
    api.get("/plan/ai-proposal/latest").then((r) => show(initialRequest ? null : r.data));
  }, [open, initialRequest, autoGenerate]);

  const generate = async (reqText = request) => {
    setBusy(true);
    try {
      const { data } = await api.post("/plan/ai-proposal", { request: reqText || "" });
      show(data);
      if (!data.changes.length) toast.info("El Coach cree que tu semana ya está bien así");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!open || !autoGenerate || autoRan.current) return;
    autoRan.current = true;
    generate(initialRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoGenerate, initialRequest]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/plan/ai-proposal/${prop.id}/accept`, { dates: sel });
      toast.success(`Semana adaptada: ${data.applied_days} día(s) actualizados`);
      show(null);
      onOpenChange(false);
      onApplied?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const discard = async () => {
    if (prop?.id) await api.post(`/plan/ai-proposal/${prop.id}/discard`).catch(() => {});
    show(null);
    onOpenChange(false);
    toast("Propuesta descartada");
  };
  const toggle = (d) => setSel(sel.includes(d) ? sel.filter((x) => x !== d) : [...sel, d]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-lg" data-testid="ai-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            <Wand2 className="h-5 w-5" /> {title || "Adaptar mi semana con IA"}
          </DialogTitle>
          <DialogDescription>
            {description || "El Coach propone cambios en tu entreno y tus comidas de los próximos días. Tú decides qué aplicar."}
          </DialogDescription>
        </DialogHeader>
        {!prop ? (
          <div className="space-y-4">
            {autoGenerate && busy ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="ai-plan-auto-loading">
                <Sparkles className="h-6 w-6 animate-spin text-ink" />
                <p className="text-sm font-semibold text-ink">Revisando tus últimos días y el plan que viene…</p>
                <p className="text-xs text-stone-500">Comparando desviaciones con lo planeado para proponerte ajustes.</p>
              </div>
            ) : (
              <>
                {!autoGenerate && (
                  <Textarea rows={3} value={request} onChange={(e) => setRequest(e.target.value)} placeholder="Opcional: «esta semana tengo poco tiempo», «me molesta la espalda», «quiero cenas más ligeras»…" className="rounded-2xl" data-testid="ai-plan-request" />
                )}
                {autoGenerate && (
                  <p className="text-sm text-stone-600" data-testid="ai-plan-auto-retry-hint">
                    No se pudo generar la propuesta automáticamente. Puedes reintentar.
                  </p>
                )}
                <button disabled={busy} onClick={() => generate()} data-testid="ai-plan-generate-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 font-semibold text-white transition-transform active:scale-95 disabled:opacity-60">
                  <Sparkles className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "El Coach está revisando tu semana…" : autoGenerate ? "Reintentar propuesta" : "Generar propuesta"}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="ai-plan-review">
            <div className="rounded-2xl bg-stone-50 p-4 text-sm">
              <p className="font-semibold text-ink" data-testid="ai-plan-summary">{prop.summary || "Propuesta del Coach"}</p>
              <p className="mt-1 text-xs text-stone-500">vía {prop.provider} · respeta tus alergias, tu dieta y tus dolencias</p>
            </div>
            {!prop.changes.length && <p className="text-sm text-stone-600" data-testid="ai-plan-no-changes">Sin cambios propuestos.</p>}
            {prop.changes.map((c, i) => <ChangeDay key={c.date} c={c} i={i} checked={sel.includes(c.date)} onToggle={() => toggle(c.date)} />)}
            <div className="flex gap-2">
              <button onClick={discard} data-testid="ai-plan-discard-btn" className="rounded-full border border-stone-300 px-5 py-3 text-sm font-semibold">Descartar</button>
              <button disabled={busy || !sel.length} onClick={accept} data-testid="ai-plan-accept-btn" className="flex-1 rounded-full bg-ink py-3 text-sm font-semibold text-white transition-transform active:scale-95 disabled:opacity-50">
                Aplicar {sel.length || 0} día(s)
              </button>
            </div>
            <button onClick={() => { show(null); autoRan.current = false; }} data-testid="ai-plan-new-btn" className="w-full text-center text-xs font-semibold text-stone-500 hover:text-ink">Pedir otra propuesta</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const AiPlanButton = ({ onApplied, testid = "ai-plan-open-btn", className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testid} className={`inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white transition-transform active:scale-95 ${className}`}>
        <Wand2 className="h-3.5 w-3.5" /> Adaptar mi semana con IA
      </button>
      <AiPlanDialog open={open} onOpenChange={setOpen} onApplied={onApplied} />
    </>
  );
};

/** Dispara la propuesta automática tras una desviación del plan. */
export const useDeviationAdapt = (onApplied) => {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState("");

  const trigger = (ctx = {}) => {
    const parts = [
      "He anotado algo distinto a lo planeado. Revisa los últimos 3-5 días (desviaciones, omisiones y alternativas) y compáralo con lo que viene.",
      "Propón cambios concretos de entrenos y/o comidas para reequilibrar la semana.",
    ];
    if (ctx.kind === "meal_replace") {
      parts.push(`Hoy (${ctx.date || "hoy"}) en ${ctx.slot || "una comida"} comí «${ctx.actual || "otra cosa"}» en vez de «${ctx.planned || "lo planeado"}». Ajusta el resto del día y los próximos si hace falta.`);
    } else if (ctx.kind === "meal_skip") {
      parts.push(`Hoy (${ctx.date || "hoy"}) no hice la comida «${ctx.slot || ""}». Reequilibra calorías/macros en lo que queda del día y próximos días.`);
    } else if (ctx.kind === "meal_extra") {
      parts.push(`Hoy (${ctx.date || "hoy"}) añadí un extra${ctx.time ? ` a las ${ctx.time}` : ""}: «${ctx.actual || "algo fuera de plan"}» (${ctx.kcal != null ? `${ctx.kcal} kcal` : "kcal n/d"}). Reequilibra el resto del día y lo que viene si conviene.`);
    } else if (ctx.kind === "workout_alt") {
      parts.push(`Hoy (${ctx.date || "hoy"}) hice otro entreno: «${ctx.actual || "alternativa"}» en vez de «${ctx.planned || "lo planeado"}». Adapta los próximos entrenos.`);
    } else if (ctx.kind === "workout_skip") {
      parts.push(`Hoy (${ctx.date || "hoy"}) no entrené (omití «${ctx.planned || "la sesión"}»). Redistribuye o suaviza los próximos días.`);
    } else if (ctx.note) {
      parts.push(ctx.note);
    }
    setRequest(parts.join(" "));
    setOpen(true);
  };

  const dialog = (
    <AiPlanDialog
      open={open}
      onOpenChange={setOpen}
      initialRequest={request}
      autoGenerate
      onApplied={onApplied}
      title="Ajustes sugeridos tras tu anotación"
      description="Hemos comparado lo que hiciste con el plan. Elige qué cambios aceptar."
    />
  );

  return { trigger, dialog };
};
